import { useEffect, useRef, useState } from "react";
import { BaseItem } from "../../interfaces/MediaAndContent";
import BoxLogos from "../Cards/BoxLogos";

interface InfiniteScrollProps<T> {
    items: BaseItem[];
    itemWidth?: number;
    gap?: number;
}

const InfiniteHorizontalAutoScroll = <T,>({
    items,
    itemWidth = 180,
    gap = 32,
}: InfiniteScrollProps<T>) => {
    const scrollRef = useRef<HTMLDivElement>(null); // This is now the container that will scroll
    const animatedRef = useRef<HTMLDivElement>(null);

    const [displayedItems, setDisplayedItems] = useState<BaseItem[]>([]);
    const [isTouching, setIsTouching] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    const isDragging = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);

    useEffect(() => {
        if (!items || items.length === 0 || !scrollRef.current) return;

        const containerWidth = scrollRef.current.clientWidth;
        const minItems = Math.ceil(containerWidth / (itemWidth + gap));

        const newItems = [];
        while (newItems.length < minItems * 15) {
            newItems.push(...items);
        }

        setDisplayedItems(newItems);
    }, [items, itemWidth, gap]);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.matchMedia("(pointer: coarse)").matches);
        };

        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    const pauseAnimation = () => {
        if (animatedRef.current) animatedRef.current.style.animationPlayState = "paused";
    };

    const resumeAnimation = () => {
        if (animatedRef.current) animatedRef.current.style.animationPlayState = "running";
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        setIsTouching(true);
        if (!scrollRef.current) return;
        isDragging.current = true;
        startX.current = e.touches[0].pageX;
        scrollLeft.current = scrollRef.current.scrollLeft;
        scrollRef.current.style.animationPlayState = "paused";
        pauseAnimation();
    };

    const handleTouchEnd = () => {
        isDragging.current = false;
        setIsTouching(false);
        resumeAnimation();
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging.current || !scrollRef.current) return;
        const x = e.touches[0].pageX;
        const walk = x - startX.current;
        scrollRef.current.scrollLeft = scrollLeft.current - walk;
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        isDragging.current = true;
        startX.current = e.pageX;
        scrollLeft.current = scrollRef.current?.scrollLeft ?? 0;
        pauseAnimation();
    };

    const handleMouseUpOrLeave = () => {
        isDragging.current = false;
        resumeAnimation();
    };

    return (
        <div
            ref={scrollRef}
            className={`relative w-full mt-6 scroll-smooth touch-pan-x overflow-y-hidden ${isTouching ? "overflow-x-scroll" : "overflow-x-hidden"
                }`}
            onMouseEnter={pauseAnimation}
            onMouseLeave={resumeAnimation}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUpOrLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <div
                ref={animatedRef}
                className="flex w-max py-3 gap-8"
                style={{
                    animationName: "scroll",
                    animationDuration: `${isMobile ? "60s" : "150s"}`,
                    animationTimingFunction: "linear",
                    animationIterationCount: "infinite",
                    animationPlayState: "running",
                }}
            >
                <BoxLogos displayedItems={displayedItems} />
            </div>
        </div>
    );
};


export default InfiniteHorizontalAutoScroll;
